import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { DynamicDialogRef, DynamicDialogConfig } from 'primeng/dynamicdialog';
import { ButtonModule } from 'primeng/button';
import { ScrollPanelModule } from 'primeng/scrollpanel';
import { DividerModule } from 'primeng/divider';
import { TagModule } from 'primeng/tag';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../../environments/environment';

export interface ChangelogEntry {
  version: string;
  date: string;
  sections: {
    type: 'added' | 'improved' | 'fixed' | 'performance' | 'security' | 'docs' | 'maintenance' | 'mobile';
    title: string;
    items: string[];
    icon: string;
    color: string;
  }[];
}

@Component({
  selector: 'app-changelog-dialog',
  standalone: true,
  imports: [
    CommonModule,
    ButtonModule,
    ScrollPanelModule,
    DividerModule,
    TagModule
  ],
  templateUrl: './changelog-dialog.component.html',
  styleUrl: './changelog-dialog.component.scss'
})
export class ChangelogDialogComponent implements OnInit {
  currentVersion = environment.version || '0.0.0';
  environmentName = environment.name;
  changelog: ChangelogEntry[] = [];

  constructor(
    public ref: DynamicDialogRef,
    public config: DynamicDialogConfig,
    private http: HttpClient
  ) {}

  ngOnInit() {
    this.loadChangelogFromFile();
  }

  private loadChangelogFromFile() {
    this.http.get('/CHANGELOG.md', { responseType: 'text' })
      .subscribe({
        next: (content) => {
          this.changelog = this.parseChangelog(content);
        },
        error: (error) => {
          console.error('Errore nel caricamento del changelog:', error);
          // Fallback con dati di default
          this.loadDefaultChangelog();
        }
      });
  }

  private parseChangelog(content: string): ChangelogEntry[] {
    const entries: ChangelogEntry[] = [];
    const lines = content.split('\n');

    let currentEntry: ChangelogEntry | null = null;
    let currentSection: any = null;

    // Mappa emoji -> tipo sezione
    const sectionMap: { [key: string]: { type: string; title: string; color: string } } = {
      '✨': { type: 'added', title: 'Aggiunto', color: '#10b981' },
      '🎨': { type: 'improved', title: 'Migliorato', color: '#6366f1' },
      '🐛': { type: 'fixed', title: 'Fix', color: '#ef4444' },
      '⚡': { type: 'performance', title: 'Performance', color: '#f59e0b' },
      '🔒': { type: 'security', title: 'Sicurezza', color: '#dc2626' },
      '📚': { type: 'docs', title: 'Documentazione', color: '#8b5cf6' },
      '🔧': { type: 'maintenance', title: 'Manutenzione', color: '#6b7280' },
      '📱': { type: 'mobile', title: 'Mobile', color: '#06b6d4' }
    };

    for (const line of lines) {
      // Verifica se è una versione: ## [0.0.55] - 2025-10-15
      const versionMatch = line.match(/^##\s+\[([^\]]+)\]\s+-\s+(\d{4}-\d{2}-\d{2})/);
      if (versionMatch) {
        // Salva l'entry precedente se esiste
        if (currentEntry && currentSection) {
          currentEntry.sections.push(currentSection);
        }
        if (currentEntry) {
          entries.push(currentEntry);
        }

        // Crea nuova entry
        currentEntry = {
          version: versionMatch[1],
          date: versionMatch[2],
          sections: []
        };
        currentSection = null;
        continue;
      }

      // Verifica se è una sezione: ### ✨ Aggiunto
      // Usa una regex più generica per catturare qualsiasi emoji
      const sectionMatch = line.match(/^###\s+([\u{1F300}-\u{1F9FF}\u{2600}-\u{26FF}\u{2700}-\u{27BF}\u{FE00}-\u{FE0F}\u{1F000}-\u{1F02F}\u{1F0A0}-\u{1F0FF}\u{1F100}-\u{1F64F}\u{1F680}-\u{1F6FF}\u{1F910}-\u{1F96B}\u{1F980}-\u{1F9E0}\u2728\u{1FA70}-\u{1FAFF}]+)\s+(.+)/u);
      if (sectionMatch && currentEntry) {
        // Salva la sezione precedente se esiste
        if (currentSection) {
          currentEntry.sections.push(currentSection);
        }

        const emoji = sectionMatch[1];
        const sectionInfo = sectionMap[emoji] || {
          type: 'other',
          title: sectionMatch[2],
          color: '#6b7280'
        };

        currentSection = {
          type: sectionInfo.type,
          title: sectionInfo.title,
          icon: emoji,
          color: sectionInfo.color,
          items: []
        };
        continue;
      }

      // Verifica se è un item: - Testo dell'item
      const itemMatch = line.match(/^-\s+(.+)/);
      if (itemMatch && currentSection) {
        currentSection.items.push(itemMatch[1]);
      }
    }

    // Salva l'ultima sezione e entry
    if (currentEntry && currentSection) {
      currentEntry.sections.push(currentSection);
    }
    if (currentEntry) {
      entries.push(currentEntry);
    }

    // Mostra tutte le versioni
    return entries;
  }

  private loadDefaultChangelog() {
    // Fallback con dati hardcoded
    this.changelog = [
      {
        version: '0.0.55',
        date: '2025-10-15',
        sections: [
          {
            type: 'added',
            title: 'Aggiunto',
            icon: '✨',
            color: '#10b981',
            items: [
              'Sistema di versionamento automatico dell\'applicazione e badge versione',
              'Import dei fascicoli estratti a campione per il controllo in LOCO',
              'Storico delle domande di sostegno e di pagamento'
            ]
          }
        ]
      }
    ];
  }

  isCurrentVersion(version: string): boolean {
    return version === this.currentVersion;
  }

  downloadChangelog() {
    // Crea un link per scaricare il CHANGELOG.md
    const link = document.createElement('a');
    link.href = '/CHANGELOG.md';
    link.download = 'CHANGELOG.md';
    link.click();
  }

  close() {
    this.ref.close();
  }
}

